import React, { useCallback, ReactElement } from 'react';
import { Form as FormInternal } from 'react-final-form';
import ErrorFormatService from '../../services/error-format-service';
import StructureService from '../../services/structure-service';
import ScreenService from '../../services/screen-service';

const FormContainer: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    formId,
    component,
    validator,
    initialValues,
    formErrors,
    isSubmitting,
    ...custom
  } = props;

  const mainFormError: string | null = ErrorFormatService.getMainServerFormError(formErrors);

  const onFormSubmit: FormSubmitFunction = useCallback((): ValidationErrors | boolean => {
    if (!formId) {
      return false;
    }

    const form: HTMLFormElement | null = ScreenService.getElementById(formId) as HTMLFormElement;
    if (!form) {
      return false;
    }

    return formErrors;
  }, [formId, formErrors]);

  const addValidator: Function = useCallback((validator: Validator, values: FormValues): ValidationErrors | boolean => {
    if (!validator) {
      return false;
    }

    const errors: ValidationErrors = validator(values);

    if (errors && Object.keys(errors).length) {
      errors[ErrorFormatService.FORM_ERROR] = '';
    }

    return errors;
  }, []);

  const prepareFormValues: Function = useCallback((formValues: FormValues): FormValues | null => {
    if (!formValues) {
      return null;
    }

    return StructureService.flattenObject(formValues);
  }, []);

  return (
    <FormInternal
      keepDirtyOnReinitialize
      component={component}
      validate={validator ? (formValues: FormValues): ValidationErrors | null => (
        addValidator(validator, formValues)
      ) : undefined}
      initialValues={initialValues ? prepareFormValues(initialValues) : undefined}
      onSubmit={onFormSubmit}
      formErrors={formErrors}
      mainFormError={mainFormError}
      isSubmitting={isSubmitting}
      {...custom}
    />
  );
});

export default FormContainer;
